import { clientServerSchema, daemonSyncSchema, hasPermission } from '@hearth/shared';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';
import { CopyChip } from '@/components/copy-chip';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { apiRequest } from '@/lib/api';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';

export const Route = createFileRoute('/_app/server/$server/settings')({
  component: ServerSettings,
});

function ServerSettings() {
  const { server: id } = Route.useParams();
  const { data: server } = useSuspenseQuery(serverQuery(id));
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (autoRestart: boolean) =>
      apiRequest(`/api/client/servers/${id}/settings`, {
        method: 'PATCH',
        body: { autoRestart },
        schema: clientServerSchema.extend(daemonSyncSchema.shape),
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(serverQuery(id).queryKey, res);
      if (!res.daemonSynced)
        toast.warning(`Saved, but the node didn't get the change yet: ${res.daemonError}`);
      else toast.success(res.autoRestart ? 'Automatic restarts on.' : 'Automatic restarts off.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="grid gap-6">
      {hasPermission(server.permissions, 'file.sftp') && (
        <Card>
          <CardHeader>
            <CardTitle>SFTP</CardTitle>
            <CardDescription>
              Connect with any SFTP client using your panel password or an SSH key from your
              account.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <dl className="grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Host</dt>
              <dd>
                <CopyChip value={server.sftp.host} label="SFTP host" />
              </dd>
              <dt className="text-muted-foreground">Port</dt>
              <dd>
                <CopyChip value={String(server.sftp.port)} label="SFTP port" />
              </dd>
              <dt className="text-muted-foreground">Username</dt>
              <dd>
                <CopyChip
                  value={server.sftp.username}
                  label="SFTP username"
                  className="break-all"
                />
              </dd>
            </dl>
            <div className="grid gap-1.5">
              <p className="text-muted-foreground text-xs">From a terminal</p>
              <CopyChip
                value={`sftp -P ${server.sftp.port} ${server.sftp.username}@${server.sftp.host}`}
                label="SFTP command"
                className="w-fit max-w-full break-all"
              />
            </div>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Crash handling</CardTitle>
          <CardDescription>
            When the server process exits without being asked to (a non-zero exit code or running
            out of memory), Hearth can start it again. After 3 crashes within a minute it stops
            trying until you start the server yourself.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Switch
            id="auto-restart"
            checked={server.autoRestart}
            disabled={save.isPending}
            onCheckedChange={(v) => save.mutate(v)}
          />
          <Label htmlFor="auto-restart">Restart automatically after a crash</Label>
        </CardContent>
      </Card>
    </div>
  );
}
